export interface ChatStylesConfig {
  primaryColor?: string;
  /** If true, generates styles for embedded mode */
  embedded?: boolean;
}

/**
 * Generate CSS styles for the chat widget
 * Injected into Shadow DOM for style isolation
 */
export function getChatStyles(config: ChatStylesConfig = {}): string {
  const primaryColor = config.primaryColor || "#0066cc";
  const embedded = config.embedded || false;

  // Base embedded styles
  const embeddedStyles = embedded
    ? `
    /* =====================================================
       EMBEDDED MODE STYLES
       ===================================================== */
    
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }

    .pankagent-embedded-container {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
    }

    .pankagent-unified-widget.embedded {
      position: relative !important;
      width: 100% !important;
      height: 100% !important;
      left: auto !important;
      top: auto !important;
      z-index: 1 !important;
      border-radius: 12px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      border: 1px solid #e5e5e5;
    }

    .pankagent-unified-widget.embedded .pankagent-unified-messages {
      flex: 1;
      min-height: 0;
    }

    .pankagent-unified-widget.embedded .pankagent-full-input-container {
      border-top: 1px solid #f0f0f0;
    }

    /* No logo button in embedded full mode */
    .pankagent-unified-widget.embedded .pankagent-full-input-left {
      padding-left: 8px;
    }
  `
    : "";

  return `
    :host {
      --pankagent-primary: ${primaryColor};
      
      /* Widget sizing */
      --pankagent-bubble-size: 60px;
      --pankagent-handle-width: 32px;
      --pankagent-margin: 24px;
      
      /* Animation timing */
      --pankagent-transition-duration: 0.5s;
      --pankagent-transition-timing: cubic-bezier(0.19, 1, 0.22, 1);
      --pankagent-enter-timing: cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    /* Container */
    .pankagent-container {
      position: fixed;
      top: 0;
      left: 0;
      width: 0;
      height: 0;
      z-index: 999999;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    /* =====================================================
       UNIFIED WIDGET - Single container with 3 modes
       ===================================================== */
    
    .pankagent-unified-widget {
      position: fixed;
      display: flex;
      flex-direction: column;
      background: #ffffff;
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
      z-index: 999999;
      overflow: hidden;
      user-select: none;
      /* All morphing properties transition together with unified timing */
      will-change: width, height, transform, right, bottom, left, top;
      transition:
        width var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        height var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        left var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        top var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        right var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        bottom var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        transform var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        border-radius var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        background 0.3s ease,
        backdrop-filter 0.3s ease,
        box-shadow var(--pankagent-transition-duration) ease;
    }

    /* Only disable touch-action for drag handles, not the whole widget */
    .pankagent-unified-widget.mode-condensed,
    .pankagent-unified-widget.mode-expanded {
      touch-action: none;
    }

    /* Initial entrance animation - scale bounce from bottom right */
    .pankagent-unified-widget.entering {
      animation: widgetEnter 0.4s var(--pankagent-enter-timing) forwards;
    }

    @keyframes widgetEnter {
      0% {
        transform: scale(0);
        opacity: 0;
      }
      100% {
        transform: scale(1);
        opacity: 1;
      }
    }

    /* Dragging state - disable transitions, elevated shadow for depth feedback */
    .pankagent-unified-widget.dragging {
      transition: box-shadow 0.2s ease;
      cursor: grabbing;
      box-shadow: 0 12px 48px rgba(0, 0, 0, 0.25);
    }

    /* Snap animation state - re-enable transitions after drag */
    .pankagent-unified-widget.snapping {
      transition:
        left var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        top var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        width var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        height var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        transform var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        border-radius var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        box-shadow 0.4s ease;
    }

    /* === CONDENSED MODE === */
    .pankagent-unified-widget.mode-condensed {
      border-radius: 30px 12px 12px 30px;
    }

    .pankagent-unified-widget.mode-condensed.edge-left {
      border-radius: 12px 30px 30px 12px;
    }

    .pankagent-unified-widget.mode-condensed .pankagent-unified-input-area {
      flex-direction: row;
      padding: 0;
      gap: 0;
      height: 100%;
    }

    .pankagent-unified-widget.mode-condensed.edge-left .pankagent-unified-input-area {
      flex-direction: row-reverse;
    }

    .pankagent-unified-widget.mode-condensed .pankagent-unified-logo {
      width: 60px;
      height: 60px;
      border-radius: 30px 0 0 30px;
      padding: 6px;
    }

    .pankagent-unified-widget.mode-condensed.edge-left .pankagent-unified-logo {
      border-radius: 0 30px 30px 0;
    }

    .pankagent-unified-widget.mode-condensed .pankagent-unified-handle {
      width: 32px;
      height: 60px;
      border-radius: 0 12px 12px 0;
    }

    .pankagent-unified-widget.mode-condensed.edge-left .pankagent-unified-handle {
      border-radius: 12px 0 0 12px;
    }

    /* === EXPANDED MODE === */
    .pankagent-unified-widget.mode-expanded {
      border-radius: 50px 25px 25px 50px;
      background: rgba(255, 255, 255, 0.67);
      backdrop-filter: blur(14.5px);
      -webkit-backdrop-filter: blur(14.5px);
      box-shadow: 0px 0px 10px 3px rgba(0, 0, 0, 0.05);
      overflow: visible;
    }

    /* Expanded mode input elements - staggered morph transitions */
    .pankagent-unified-widget.mode-expanded .pankagent-expanded-input-group {
      transition: opacity 0.3s ease, transform 0.35s var(--pankagent-enter-timing);
      opacity: 1;
      transform: scale(1) translateX(0);
    }

    /* Hidden state when transitioning to expanded (coming from condensed) */
    .pankagent-unified-widget.mode-condensed .pankagent-expanded-input-group {
      opacity: 0;
      transform: scale(0.9) translateX(-20px);
    }

    /* Staggered appearance of child elements */
    .pankagent-unified-widget.mode-expanded .pankagent-attach-container {
      transition: opacity 0.25s ease 0.05s, transform 0.3s var(--pankagent-enter-timing) 0.05s;
    }

    .pankagent-unified-widget.mode-expanded .pankagent-input-container {
      transition: opacity 0.25s ease 0.1s, transform 0.3s var(--pankagent-enter-timing) 0.1s;
    }

    .pankagent-unified-widget.mode-expanded .pankagent-unified-handle {
      transition: opacity 0.25s ease 0.15s, transform 0.3s var(--pankagent-enter-timing) 0.15s, color 0.2s ease;
    }

    .pankagent-unified-widget.mode-expanded .pankagent-unified-input-area {
      flex-direction: row;
      padding: 8px 12px 8px 21px;
      gap: 12px;
      height: 100%;
      align-items: center;
    }

    .pankagent-unified-widget.mode-expanded .pankagent-unified-logo {
      width: 40px;
      height: 40px;
      padding: 0;
      flex-shrink: 0;
      background: transparent;
      overflow: visible;
      margin-left: -6px;
    }

    .pankagent-unified-widget.mode-expanded .pankagent-unified-logo svg {
      width: 48px;
      height: 48px;
      filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.12));
    }

    .pankagent-unified-widget.mode-expanded .pankagent-unified-input {
      flex: 1;
      opacity: 1;
      transform: translateX(0);
    }

    .pankagent-unified-widget.mode-expanded .pankagent-unified-handle {
      width: 37px;
      height: 37px;
      margin-left: 0;
      flex-shrink: 0;
      color: rgba(124, 155, 177, 0.4);
    }

    /* Input container styling for expanded mode */
    .pankagent-unified-widget.mode-expanded .pankagent-expanded-input-group {
      display: flex;
      flex: 1;
      align-items: center;
      gap: 4px;
    }

    /* Attach button container in expanded mode */
    .pankagent-unified-widget.mode-expanded .pankagent-attach-container {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 47px;
      width: 47px;
      padding: 0;
      background: #ffffff;
      border: 1px solid #f4f4f4;
      border-radius: 51px;
      box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.2);
      flex-shrink: 0;
    }

    .pankagent-unified-widget.mode-expanded .pankagent-attach-btn {
      width: 33px;
      height: 33px;
      border-radius: 50px;
      background: #444343;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.5);
      transition: transform 0.2s ease;
      position: relative;
    }

    .pankagent-unified-widget.mode-expanded .pankagent-attach-btn:hover {
      transform: scale(1.05);
    }

    .pankagent-unified-widget.mode-expanded .pankagent-attach-btn svg {
      width: 16.5px;
      height: 16.5px;
      color: white;
    }

    /* File attachment badge in expanded mode */
    .pankagent-attach-badge {
      position: absolute;
      top: -2px;
      right: -2px;
      width: 10px;
      height: 10px;
      background: #34B8C1;
      border-radius: 50%;
      border: 2px solid #ffffff;
      box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.2);
    }

    /* Input field container in expanded mode */
    .pankagent-unified-widget.mode-expanded .pankagent-input-container {
      display: flex;
      flex: 1;
      align-items: center;
      justify-content: space-between;
      height: 47px;
      padding: 0 6px 0 25px;
      background: #ffffff;
      border: 1px solid #f4f4f4;
      border-radius: 51px;
      box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.2);
    }

    .pankagent-unified-widget.mode-expanded .pankagent-input-container input {
      flex: 1;
      border: none;
      background: transparent;
      font-size: 16.9px;
      font-family: 'Inter', sans-serif;
      font-weight: 500;
      color: #1a1a1a;
      outline: none;
      min-width: 0;
    }

    .pankagent-unified-widget.mode-expanded .pankagent-input-container input::placeholder {
      color: rgba(124, 155, 177, 0.8);
    }

    .pankagent-unified-widget.mode-expanded .pankagent-send-btn {
      width: 33px;
      height: 33px;
      flex-shrink: 0;
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.2s ease, opacity 0.2s ease;
    }

    .pankagent-unified-widget.mode-expanded .pankagent-send-btn:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    .pankagent-unified-widget.mode-expanded .pankagent-send-btn:not(:disabled):hover {
      transform: scale(1.05);
    }

    .pankagent-unified-widget.mode-expanded .pankagent-send-btn svg {
      width: 33px;
      height: 33px;
    }

    /* === FULL MODE === */
    .pankagent-unified-widget.mode-full {
      border-radius: 24px;
      max-width: min(80vw, 1280px);
      max-height: 80vh;
      background: #ffffff;
      box-shadow: 0px 0px 12px 4px rgba(0, 0, 0, 0.05);
    }

    /* === REDUCED MODE (full mode with smaller width in corner) === */
    .pankagent-unified-widget.mode-full.reduced {
      border-radius: 20px;
      max-width: calc(100vw - 48px);
      max-height: calc(100vh - 48px);
    }

    /* Reduced mode: smaller horizontal paddings for chat window */
    .pankagent-unified-widget.mode-full.reduced .pankagent-unified-messages {
      padding-left: 16px;
      padding-right: 16px;
    }

    /* Reduced mode: smaller avatar for messages to save space */
    .pankagent-unified-widget.mode-full.reduced .pankagent-message-avatar {
      width: 24px;
      height: 24px;
    }

    .pankagent-unified-widget.mode-full.reduced .pankagent-message-avatar svg {
      width: 28px;
      height: 28px;
    }

    /* Reduced mode: messages take more width */
    .pankagent-unified-widget.mode-full.reduced .pankagent-message.assistant {
      max-width: 95%;
      gap: 8px;
    }

    .pankagent-unified-widget.mode-full.reduced .pankagent-message.user {
      max-width: 95%;
    }

    /* Reduced mode: smaller typing indicator avatar */
    .pankagent-unified-widget.mode-full.reduced .typing-indicator-message .pankagent-message-avatar {
      width: 24px;
      height: 24px;
    }

    .pankagent-unified-widget.mode-full.reduced .typing-indicator-message .pankagent-message-avatar svg {
      width: 28px;
      height: 28px;
    }

    /* Reduced mode: smaller numbered list indicators */
    .pankagent-unified-widget.mode-full.reduced .pankagent-md-list-number {
      width: 24px;
      height: 24px;
      min-width: 24px;
      border-width: 1.5px;
    }

    .pankagent-unified-widget.mode-full.reduced .pankagent-md-list-number span {
      font-size: 14px;
    }

    .pankagent-unified-widget.mode-full.reduced .pankagent-md-list-item {
      gap: 12px;
    }

    .pankagent-unified-widget.mode-full.reduced .pankagent-md-ordered-list {
      gap: 10px;
      margin: 12px 0;
    }

    .pankagent-unified-widget.mode-full.reduced .pankagent-md-list-content {
      font-size: 14px;
    }

    /* Reduced mode: smaller text in message content */
    .pankagent-unified-widget.mode-full.reduced .pankagent-message-content {
      font-size: 14px;
      padding: 14px 18px;
      border-radius: 18px;
    }

    /* Reduced mode: smaller paragraph text */
    .pankagent-unified-widget.mode-full.reduced .pankagent-md-paragraph {
      font-size: 14px;
    }

    /* Reduced mode: smaller bold text */
    .pankagent-unified-widget.mode-full.reduced .pankagent-md-bold {
      font-size: 14px;
    }

    /* Reduced mode: smaller markdown content gap */
    .pankagent-unified-widget.mode-full.reduced .pankagent-md-content {
      gap: 12px;
    }

    /* Reduced mode: smaller user message (same styling as message-content in reduced) */
    .pankagent-unified-widget.mode-full.reduced .pankagent-message.user {
      font-size: 14px;
      padding: 14px 18px;
      border-radius: 18px;
    }

    /* Reduced mode: smaller input container */
    .pankagent-unified-widget.mode-full.reduced .pankagent-full-input-container {
      min-height: 75px;
      padding: 12px 16px;
    }

    .pankagent-unified-widget.mode-full.reduced .pankagent-full-input-textarea {
      min-height: 40px;
      font-size: 14px;
    }

    .pankagent-unified-widget.mode-full.reduced .pankagent-full-input-actions {
      padding-top: 8px;
    }

    .pankagent-unified-widget.mode-full.reduced .pankagent-full-logo svg {
      width: 32px;
      height: 32px;
    }

    /* Full mode input container - staggered entrance with scale effect */
    .pankagent-unified-widget.mode-full .pankagent-full-input-container {
      opacity: 1;
      transform: translateY(0) scale(1);
      transition: opacity 0.3s ease 0.15s, transform 0.4s var(--pankagent-enter-timing) 0.1s;
    }

    /* Hidden state when coming from condensed/expanded */
    .pankagent-unified-widget:not(.mode-full) .pankagent-full-input-container {
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }

    .pankagent-unified-widget.mode-full .pankagent-unified-input-area {
      flex-direction: column;
      padding: 12px;
      gap: 9px;
      align-items: stretch;
      border-top: none;
      flex-shrink: 0;
      background: transparent;
    }

    .pankagent-unified-widget.mode-full .pankagent-unified-logo {
      width: 32px;
      height: 32px;
      padding: 0;
      flex-shrink: 0;
    }

    /* Full mode input container - matches Figma design */
    .pankagent-full-input-container {
      background: #ffffff;
      border: 1px solid rgba(0, 0, 0, 0.08);
      border-radius: 16px;
      box-shadow: 0px 4px 20px -5px rgba(0, 0, 0, 0.12);
      min-height: 150px;
      padding: 16px 20px 16px 20px;
      display: flex;
      flex-direction: column;
      position: relative;
    }

    .pankagent-full-input-textarea {
      flex: 1;
      border: none;
      background: transparent;
      font-size: 16px;
      font-family: 'Inter', sans-serif;
      font-weight: 500;
      color: #1a1a1a;
      outline: none;
      resize: none;
      line-height: 1.5;
      min-height: 100px;
    }

    .pankagent-full-input-textarea::placeholder {
      color: rgba(124, 155, 177, 0.8);
    }

    .pankagent-full-input-actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: auto;
      padding-top: 12px;
    }

    .pankagent-full-input-left {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .pankagent-full-logo {
      width: 32px;
      height: 32px;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: none;
      cursor: pointer;
      transition: transform 0.2s ease, opacity 0.2s ease;
    }

    .pankagent-full-logo:hover {
      transform: scale(1.05);
      opacity: 0.9;
    }

    .pankagent-full-logo svg {
      width: 40px;
      height: 40px;
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
    }

    .pankagent-full-attach-btn {
      width: 31px;
      height: 31px;
      border-radius: 50%;
      background: #444343;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.5);
      transition: transform 0.2s ease;
    }

    .pankagent-full-attach-btn:hover {
      transform: scale(1.05);
    }

    .pankagent-full-attach-btn svg {
      width: 16px;
      height: 16px;
      color: white;
    }

    /* File attachment info in full mode */
    .pankagent-file-info {
      display: flex;
      align-items: center;
      gap: 6px;
      max-width: 160px;
      background: rgba(68, 67, 67, 0.1);
      border-radius: 8px;
      padding: 4px 8px 4px 12px;
    }

    .pankagent-file-name {
      max-width: 128px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 13px;
      font-family: 'Inter', sans-serif;
      color: #444343;
    }

    .pankagent-file-remove {
      width: 20px;
      height: 20px;
      min-width: 20px;
      padding: 0;
      background: transparent;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      color: #7C9BB1;
      transition: background 0.2s ease, color 0.2s ease;
    }

    .pankagent-file-remove:hover {
      background: rgba(68, 67, 67, 0.15);
      color: #444343;
    }

    .pankagent-file-remove svg {
      width: 14px;
      height: 14px;
    }

    .pankagent-full-send-btn {
      width: 33px;
      height: 33px;
      min-width: 33px;
      padding: 0;
      background: transparent;
      border-radius: 50%;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.2s ease, opacity 0.2s ease;
    }

    .pankagent-full-send-btn:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    .pankagent-full-send-btn:not(:disabled):hover {
      transform: scale(1.05);
    }

    .pankagent-full-send-btn svg {
      width: 33px;
      height: 33px;
    }

    /* === HEADER === */
    .pankagent-unified-header {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding: 12px 15px;
      z-index: 10;
      opacity: 0;
      transform: translateY(-10px) scale(0.95);
      pointer-events: none;
      transition: 
        opacity 0.3s ease,
        transform 0.4s var(--pankagent-enter-timing);
      background: rgba(255, 255, 255, 0.67);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      border-radius: 24px 24px 0 0;
    }

    .pankagent-unified-widget.mode-full.elements-visible .pankagent-unified-header {
      opacity: 1;
      transform: translateY(0) scale(1);
      pointer-events: auto;
      transition-delay: 0.15s;
    }

    .pankagent-unified-widget.dragging .pankagent-unified-header {
      cursor: grabbing;
    }

    .pankagent-unified-header-actions {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    /* Header button base styles - hidden by default, revealed with stagger */
    .pankagent-unified-header-btn {
      background: none;
      border: none;
      color: rgba(124, 155, 177, 0.6);
      cursor: pointer;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transform: scale(0.8);
      transition: 
        opacity 0.25s ease,
        transform 0.3s var(--pankagent-enter-timing),
        color 0.2s ease;
    }

    /* Staggered reveal for header buttons in full mode */
    .pankagent-unified-widget.mode-full.elements-visible .pankagent-unified-header-btn:first-child {
      opacity: 1;
      transform: scale(1);
      transition-delay: 0.2s;
    }

    .pankagent-unified-widget.mode-full.elements-visible .pankagent-unified-header-btn:last-child {
      opacity: 1;
      transform: scale(1);
      transition-delay: 0.25s;
    }

    .pankagent-unified-header-btn:hover {
      color: rgba(124, 155, 177, 1);
    }

    .pankagent-unified-header-btn.drag-handle {
      cursor: grab;
    }

    .pankagent-unified-header-btn.drag-handle:active {
      cursor: grabbing;
    }

    /* === MESSAGES === */
    .pankagent-unified-messages {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 56px 28px 20px;
      display: flex;
      flex-direction: column;
      gap: 0; /* We use margins for animation control */
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
      min-height: 0; /* Important for flex scrolling */
    }

    /* Push messages to bottom when content is shorter than container */
    .pankagent-unified-messages::before {
      content: '';
      flex: 1 1 auto;
    }

    /* Messages container content wrapper for smooth push animation */
    .pankagent-unified-messages > * {
      transition: transform 0.3s cubic-bezier(0.34, 1.2, 0.64, 1);
    }

    /* Message spacing */
    .pankagent-unified-messages .pankagent-message {
      margin-top: 20px;
    }

    .pankagent-unified-messages .pankagent-message:first-child {
      margin-top: 0;
    }

    /* Typing indicator message */
    .pankagent-message.typing-indicator-message {
      animation: fadeIn 0.3s ease-out;
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    .pankagent-unified-widget.mode-full .pankagent-unified-messages {
      opacity: 1;
      transform: translateY(0);
      transition: opacity 0.3s ease 0.1s, transform 0.4s var(--pankagent-enter-timing) 0.05s;
    }

    /* Hidden state for messages when not in full mode */
    .pankagent-unified-widget:not(.mode-full) .pankagent-unified-messages {
      opacity: 0;
      transform: translateY(20px);
    }

    /* === INPUT AREA === */
    .pankagent-unified-input-area {
      display: flex;
      align-items: center;
      background: transparent;
      transition: 
        padding var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        gap var(--pankagent-transition-duration) var(--pankagent-transition-timing);
    }

    /* === LOGO BUTTON === */
    .pankagent-unified-logo {
      background: #ffffff;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      padding: 0;
      /* Smooth morphing between modes */
      transition: 
        transform 0.3s var(--pankagent-enter-timing), 
        opacity 0.25s ease,
        width var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        height var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        border-radius var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        padding var(--pankagent-transition-duration) var(--pankagent-transition-timing);
    }

    .pankagent-unified-logo:hover {
      transform: scale(1.05);
    }

    .pankagent-unified-logo:active {
      transform: scale(0.95);
    }

    .pankagent-unified-logo svg {
      width: 100%;
      height: 100%;
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
      transition: 
        filter 0.2s ease, 
        transform 0.3s var(--pankagent-enter-timing),
        width var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        height var(--pankagent-transition-duration) var(--pankagent-transition-timing);
    }

    .pankagent-unified-logo:hover svg {
      filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15));
    }

    /* === DIVIDER === */
    .pankagent-unified-divider {
      width: 1px;
      height: 32px;
      background: #E5E5E5;
      flex-shrink: 0;
      opacity: 0;
      transform: scaleY(0);
      transition: opacity 0.3s ease, transform 0.3s ease;
    }

    .pankagent-unified-widget.mode-expanded .pankagent-unified-divider,
    .pankagent-unified-widget.mode-full .pankagent-unified-divider {
      opacity: 1;
      transform: scaleY(1);
    }

    /* === ICON BUTTON === */
    .pankagent-unified-icon-btn {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: none;
      background: transparent;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #7C9BB1;
      flex-shrink: 0;
      opacity: 0;
      transform: scale(0.8);
      transition: opacity 0.3s ease, transform 0.3s ease, background 0.2s ease;
    }

    /* Attach button specific styles */
    .pankagent-unified-icon-btn.attach {
      width: 32px;
      height: 32px;
      padding: 0;
    }

    .pankagent-unified-icon-btn.attach svg {
      width: 32px;
      height: 32px;
    }

    .pankagent-unified-widget.mode-expanded .pankagent-unified-icon-btn,
    .pankagent-unified-widget.mode-full .pankagent-unified-icon-btn {
      opacity: 1;
      transform: scale(1);
    }

    .pankagent-unified-icon-btn:hover {
      background: rgba(0, 0, 0, 0.05);
    }

    /* === INPUT FIELD === */
    .pankagent-unified-input {
      flex: 1;
      border: none;
      background: transparent;
      font-size: 16px;
      color: #1a1a1a;
      outline: none;
      font-family: inherit;
      padding: 12px 0;
      min-width: 0;
      opacity: 0;
      transform: translateX(-20px);
      transition: opacity 0.3s ease 0.1s, transform 0.3s ease 0.1s;
    }

    .pankagent-unified-widget.mode-expanded .pankagent-unified-input,
    .pankagent-unified-widget.mode-full .pankagent-unified-input {
      opacity: 1;
      transform: translateX(0);
    }

    .pankagent-unified-input::placeholder {
      color: #7C9BB1;
      opacity: 0.7;
    }

    /* === SEND BUTTON === */
    .pankagent-unified-send {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: none;
      background: transparent;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      opacity: 0;
      transform: scale(0.8);
      transition: opacity 0.3s ease, transform 0.3s ease;
    }

    .pankagent-unified-widget.mode-expanded .pankagent-unified-send,
    .pankagent-unified-widget.mode-full .pankagent-unified-send {
      opacity: 1;
      transform: scale(1);
    }

    .pankagent-unified-send:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .pankagent-unified-send:not(:disabled):hover {
      transform: scale(1.1);
    }

    .pankagent-unified-send:not(:disabled):active {
      transform: scale(0.95);
    }

    .pankagent-unified-send svg {
      width: 32px;
      height: 32px;
    }

    /* === DRAG HANDLE === */
    .pankagent-unified-handle {
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: grab;
      color: #c5d0db;
      flex-shrink: 0;
      transition: 
        color 0.2s ease, 
        opacity 0.3s ease var(--pankagent-transition-timing), 
        width var(--pankagent-transition-duration) var(--pankagent-transition-timing), 
        height var(--pankagent-transition-duration) var(--pankagent-transition-timing),
        transform 0.3s var(--pankagent-enter-timing);
    }

    /* Handle hidden in full mode with smooth collapse */
    .pankagent-unified-widget.mode-full .pankagent-unified-handle {
      opacity: 0;
      width: 0;
      transform: scale(0.5) rotate(-45deg);
      pointer-events: none;
    }

    .pankagent-unified-handle:hover {
      color: #a0b0c0;
    }

    .pankagent-unified-widget.dragging .pankagent-unified-handle {
      cursor: grabbing;
      color: #a0b0c0;
    }

    /* === INITIAL ANIMATION === */
    @keyframes widgetEnter {
      from {
        opacity: 0;
        transform: scale(0.8) translateY(20px);
      }
      to {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
    }

    .pankagent-unified-widget {
      animation: widgetEnter 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    }

    /* === ELEMENTS VISIBILITY TRANSITIONS === */
    .pankagent-unified-widget.elements-visible .pankagent-unified-divider {
      transition-delay: 0.1s;
    }

    .pankagent-unified-widget.elements-visible .pankagent-unified-icon-btn {
      transition-delay: 0.15s;
    }

    .pankagent-unified-widget.elements-visible .pankagent-unified-input {
      transition-delay: 0.1s;
    }

    .pankagent-unified-widget.elements-visible .pankagent-unified-send {
      transition-delay: 0.2s;
    }

    /* === BORDER/BACKGROUND CONTAINER for mode-full === */
    .pankagent-unified-widget.mode-full .pankagent-unified-input-area {
      background: linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%);
      margin-top: auto;
    }

    /* === SCROLLBAR STYLING === */
    .pankagent-unified-messages::-webkit-scrollbar {
      width: 6px;
    }

    .pankagent-unified-messages::-webkit-scrollbar-track {
      background: transparent;
    }

    .pankagent-unified-messages::-webkit-scrollbar-thumb {
      background: #c5d0db;
      border-radius: 3px;
    }

    .pankagent-unified-messages::-webkit-scrollbar-thumb:hover {
      background: #a0b0c0;
    }

    /* =====================================================
       MESSAGE STYLES (shared)
       ===================================================== */

    /* Message Bubble - base styles without animation */
    .pankagent-message {
      max-width: 80%;
      position: relative;
      opacity: 1;
      transform: translateY(0);
    }

    /* Animation for NEW messages being added (not on initial load) */
    .pankagent-unified-widget.mode-full .pankagent-unified-messages .pankagent-message.new-message {
      animation: newMessageIn 0.4s cubic-bezier(0.34, 1.4, 0.64, 1) forwards;
    }

    .pankagent-unified-widget.mode-full .pankagent-unified-messages .pankagent-message.user.new-message {
      animation: newUserMessageIn 0.4s cubic-bezier(0.34, 1.4, 0.64, 1) forwards;
      transform-origin: bottom right;
    }

    .pankagent-unified-widget.mode-full .pankagent-unified-messages .pankagent-message.assistant.new-message {
      animation: newAssistantMessageIn 0.45s cubic-bezier(0.34, 1.4, 0.64, 1) forwards;
      transform-origin: bottom left;
    }

    /* Keyframes for NEW messages */
    @keyframes newMessageIn {
      0% {
        opacity: 0;
        transform: translateY(16px) scale(0.96);
      }
      100% {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    @keyframes newUserMessageIn {
      0% {
        opacity: 0;
        transform: translateY(20px) translateX(8px) scale(0.94);
      }
      70% {
        opacity: 1;
        transform: translateY(-2px) translateX(0) scale(1.01);
      }
      100% {
        opacity: 1;
        transform: translateY(0) translateX(0) scale(1);
      }
    }

    @keyframes newAssistantMessageIn {
      0% {
        opacity: 0;
        transform: translateY(20px) translateX(-8px) scale(0.94);
      }
      70% {
        opacity: 1;
        transform: translateY(-2px) translateX(0) scale(1.01);
      }
      100% {
        opacity: 1;
        transform: translateY(0) translateX(0) scale(1);
      }
    }

    /* User message - right aligned, same structure as assistant but with different background */
    .pankagent-message.user {
      align-self: flex-end;
      margin-right: 8px;
      max-width: 85%;
      background: #6E7BB2;
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      border-radius: 24px;
      padding: 20px 24px;
      box-shadow: 0px 4px 36px -5px rgba(0, 0, 0, 0.1);
      font-size: 16px;
      font-family: 'Inter', sans-serif;
      font-weight: 500;
      line-height: 1.5;
      color: white;
      word-wrap: break-word;
      position: relative;
    }

    /* User message speech bubble tail */
    .pankagent-message.user::after {
      content: '';
      position: absolute;
      bottom: 0px;
      right: -2px;
      width: 12px;
      height: 12px;
      background: #6E7BB2;
      border-radius: 50%;
    }

    .pankagent-message.user::before {
      content: '';
      position: absolute;
      bottom: 0;
      right: -10px;
      width: 6px;
      height: 6px;
      background: #6E7BB2;
      border-radius: 50%;
    }

    /* Assistant message - left aligned with avatar and frosted glass */
    .pankagent-message.assistant {
      align-self: flex-start;
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      gap: 16px;
      background: transparent;
      padding: 0;
      max-width: 85%;
    }

    .pankagent-message-avatar {
      width: 46px;
      height: 46px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .pankagent-message-avatar svg {
      width: 56px;
      height: 56px;
      filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1));
    }

    .pankagent-message-content {
      background: rgba(255, 255, 255, 0.67);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      border-radius: 24px;
      padding: 20px 24px;
      box-shadow: 0px 4px 36px -5px rgba(0, 0, 0, 0.1);
      font-size: 16px;
      font-family: 'Inter', sans-serif;
      font-weight: 500;
      line-height: 1.5;
      color: #7C9BB1;
    }

    .pankagent-message-content .highlight {
      color: #3EC2CB;
    }

    /* =====================================================
       MARKDOWN CONTENT STYLES
       ===================================================== */
    
    .pankagent-md-content {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .pankagent-md-paragraph {
      margin: 0;
      white-space: pre-wrap;
    }

    /* Bold text - colored based on agent variant */
    .pankagent-md-bold {
      font-weight: 600;
      color: #34B8C1; /* Default teal */
    }

    .pankagent-md-bold.variant-default {
      color: #34B8C1;
    }

    .pankagent-md-bold.variant-green {
      color: #34B8C1;
    }

    .pankagent-md-bold.variant-pink {
      color: #CB3EB8;
    }

    /* Ordered list container */
    .pankagent-md-ordered-list {
      display: flex;
      flex-direction: column;
      gap: 14px;
      margin: 16px 0;
    }

    /* Individual list item */
    .pankagent-md-list-item {
      display: flex;
      align-items: center;
      gap: 20px;
    }

    /* Circular number indicator */
    .pankagent-md-list-number {
      width: 42px;
      height: 42px;
      min-width: 42px;
      border-radius: 50%;
      border: 2px solid #34B8C1;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .pankagent-md-list-number span {
      font-family: 'Inter', sans-serif;
      font-weight: 700;
      font-size: 24px;
      color: #7C9BB1;
      line-height: 1;
    }

    .pankagent-md-list-number.variant-default {
      border-color: #34B8C1;
    }

    .pankagent-md-list-number.variant-green {
      border-color: #34B8C1;
    }

    .pankagent-md-list-number.variant-pink {
      border-color: #CB3EB8;
    }

    /* List item content */
    .pankagent-md-list-content {
      font-family: 'Inter', sans-serif;
      font-weight: 500;
      font-size: 16px;
      color: #7C9BB1;
      line-height: 1.5;
    }

    /* Typing Indicator */
    .pankagent-typing {
      display: flex;
      gap: 6px;
      padding: 8px 0;
      align-items: center;
    }

    .pankagent-typing-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #7C9BB1;
      animation: typingPulse 1.5s infinite ease-in-out;
    }

    .pankagent-typing-dot:nth-child(1) {
      animation-delay: 0s;
    }

    .pankagent-typing-dot:nth-child(2) {
      animation-delay: 0.15s;
    }

    .pankagent-typing-dot:nth-child(3) {
      animation-delay: 0.3s;
    }

    @keyframes typingPulse {
      0%, 100% {
        transform: scale(1) translateY(0);
        opacity: 0.4;
      }
      25% {
        transform: scale(1.2) translateY(-5px);
        opacity: 1;
      }
      50% {
        transform: scale(1) translateY(0);
        opacity: 0.7;
      }
    }

    /* =====================================================
       RESPONSIVE & ACCESSIBILITY
       ===================================================== */

    @media (max-width: 680px) {
      .pankagent-unified-widget.mode-expanded {
        width: calc(100vw - 48px) !important;
      }

      .pankagent-unified-widget.mode-full {
        width: calc(100vw - 32px) !important;
        height: calc(100vh - 120px) !important;
      }

      .pankagent-unified-messages {
        padding: 50px 16px 16px;
      }

      .pankagent-unified-widget.mode-full .pankagent-unified-input-area {
        padding: 8px;
      }

      .pankagent-full-input-container {
        min-height: 140px;
        padding: 14px 12px 10px;
      }

      .pankagent-message.user {
        max-width: 90%;
      }

      .pankagent-message.assistant {
        max-width: 95%;
        gap: 10px;
      }

      .pankagent-message-avatar {
        width: 36px;
        height: 36px;
      }

      .pankagent-message-avatar svg {
        width: 44px;
        height: 44px;
      }

      .pankagent-message-content {
        padding: 14px 18px;
        font-size: 14px;
      }

      /* Markdown responsive */
      .pankagent-md-list-number {
        width: 36px;
        height: 36px;
        min-width: 36px;
      }

      .pankagent-md-list-number span {
        font-size: 20px;
      }

      .pankagent-md-list-item {
        gap: 14px;
      }

      .pankagent-md-list-content {
        font-size: 14px;
      }

      .pankagent-md-ordered-list {
        gap: 10px;
        margin: 12px 0;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .pankagent-unified-widget,
      .pankagent-unified-widget.animating,
      .pankagent-unified-header,
      .pankagent-unified-messages,
      .pankagent-unified-divider,
      .pankagent-unified-icon-btn,
      .pankagent-unified-input,
      .pankagent-unified-send,
      .pankagent-unified-handle,
      .pankagent-message {
        transition: none !important;
        animation: none !important;
      }
    }

    ${embeddedStyles}
  `;
}
